import dayjs from 'dayjs'
import { Box, CircularProgress, Grid, Typography } from '@mui/material'
import { LineChart } from '@mui/x-charts/LineChart'
import { useGetFirebaseMostAccessedFeaturesQuery, useGetFirebaseDailyUsersQuery } from 'store/dashboard'
import InfoTooltip from 'components/InfoTooltip'
import NoDataAvailable from './NoDataAvailable'
import { useTimeRange } from './TimeRangeContext'

const RANK_COLORS = ['#F0A500', '#002C5F', '#00AAD2']

const TopRankCard = ({ rank, name, count }: { rank: number; name: string; count: number }) => {
  const color = RANK_COLORS[rank] ?? '#888'
  return (
    <Box
      sx={{
        borderRadius: 2,
        border: '1px solid #EAECF0',
        borderBottom: `3px solid ${color}`,
        p: 2,
        height: '100%',
      }}
    >
      <Box
        sx={{
          bgcolor: color, color: '#fff', fontSize: 11, fontWeight: 700,
          px: 1, py: 0.25, borderRadius: 1, lineHeight: 1.5,
          display: 'inline-block', mb: 1,
        }}
      >
        #{rank + 1}
      </Box>
      <Typography variant='caption' color='text.secondary' display='block' noWrap sx={{ mb: 0.25 }}>
        {name}
      </Typography>
      <Typography variant='h5' sx={{ fontWeight: 800, color: '#002C5F' }}>
        {count.toLocaleString()}
      </Typography>
    </Box>
  )
}

const FeatureRow = ({
  rank, name, count, maxCount,
}: {
  rank: number; name: string; count: number; maxCount: number
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75 }}>
    <Typography sx={{ minWidth: 18, fontSize: 12, color: 'text.disabled', fontWeight: 500, flexShrink: 0 }}>
      {rank}
    </Typography>
    <Typography
      variant='body2'
      sx={{ fontWeight: 500, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
    >
      {name}
    </Typography>
    <Box sx={{ width: 64, height: 4, borderRadius: 2, bgcolor: '#F0F0F0', overflow: 'hidden', flexShrink: 0 }}>
      <Box
        sx={{
          height: '100%', bgcolor: '#002C5F', borderRadius: 2,
          width: `${(count / maxCount) * 100}%`,
          transition: 'width 0.4s ease',
        }}
      />
    </Box>
    <Typography sx={{ minWidth: 28, textAlign: 'right', fontWeight: 600, flexShrink: 0, fontSize: 12 }}>
      {count}
    </Typography>
  </Box>
)

const MostAccessedFeaturesChart = () => {
  const { startDate, endDate } = useTimeRange()
  const { data, isLoading, isError, refetch } = useGetFirebaseMostAccessedFeaturesQuery({ startDate, endDate })
  const { data: daily } = useGetFirebaseDailyUsersQuery({ startDate, endDate })

  const features = data?.features ?? []
  const top3 = features.slice(0, 3)
  const rest = features.slice(3)
  const half = Math.ceil(rest.length / 2)
  const leftCol = rest.slice(0, half)
  const rightCol = rest.slice(half)
  const maxCount = Math.max(rest[0]?.accessCount ?? 1, 1)

  const formattedDate = dayjs(startDate).format('MMM YYYY')

  const formatPeakDay = (day: string | null, count: number) => {
    if (!day) return '—'
    const formatted = day.length === 8 ? dayjs(day, 'YYYYMMDD').format('MMM D') : day
    return `${formatted} · ${count.toLocaleString()}`
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (data && !data.isConfigured) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
        <Typography color='text.disabled'>Firebase not configured</Typography>
      </Box>
    )
  }

  if (isError || !data) return <NoDataAvailable onRefresh={refetch} />

  return (
    <Box>
      {/* Top 3 ranking cards */}
      {features.length > 0 && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {top3.map((f, i) => (
            <Grid item xs={12} sm={4} key={f.featureName}>
              <TopRankCard rank={i} name={f.featureName} count={f.accessCount} />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Feature list — 2 columns */}
      {rest.length > 0 && (
        <Grid container spacing={0} sx={{ mb: 3 }}>
          <Grid item xs={6} sx={{ pr: 1.5 }}>
            {leftCol.map((f, i) => (
              <FeatureRow
                key={f.featureName}
                rank={i + 4}
                name={f.featureName}
                count={f.accessCount}
                maxCount={maxCount}
              />
            ))}
          </Grid>
          <Grid item xs={6} sx={{ pl: 1.5, borderLeft: '1px solid #F0F0F0' }}>
            {rightCol.map((f, i) => (
              <FeatureRow
                key={f.featureName}
                rank={i + 4 + half}
                name={f.featureName}
                count={f.accessCount}
                maxCount={maxCount}
              />
            ))}
          </Grid>
        </Grid>
      )}

      {features.length === 0 && (
        <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
          No feature data for selected period
        </Typography>
      )}

      {/* Feature Usage Summary — dark navy header + light inner box */}
      <Box sx={{ bgcolor: '#002C5F', borderRadius: 2, overflow: 'hidden' }}>
        {/* Dark header */}
        <Box sx={{ px: 2.5, pt: 2.5, pb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
            <Typography sx={{ color: 'white', fontWeight: 700, fontSize: 15 }}>
              Feature Usage Summary
            </Typography>
            <InfoTooltip
              title='Summarizes overall feature engagement and usage trends'
              iconSize={14}
              iconColor='rgba(255,255,255,0.7)'
              variant='light'
            />
          </Box>
          <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: 12 }}>
            {formattedDate} — Daily View
          </Typography>
        </Box>

        {/* Light inner content box */}
        <Box sx={{ bgcolor: '#EBF5FF', mx: 1.5, mb: 1.5, borderRadius: 1.5, p: 2 }}>
          {/* Total Feature Accesses: label+chip top row, big number below */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
              Total Feature Accesses
            </Typography>
            <Box
              sx={{
                display: 'inline-flex', alignItems: 'center',
                bgcolor: '#E8F5E9', color: '#2E7D32',
                borderRadius: '10px', px: 1, py: '2px',
                fontSize: 11, fontWeight: 700, flexShrink: 0, ml: 1,
              }}
            >
              ↑ +{data.totalFeatureAccessesChangePercent.toFixed(1)}%
            </Box>
          </Box>
          <Typography sx={{ fontWeight: 800, fontSize: 28, lineHeight: 1.1, color: '#002C5F', mb: 2 }}>
            {data.totalFeatureAccesses.toLocaleString()}
          </Typography>

          {/* Summary rows */}
          {[
            { color: '#1976D2', label: 'Avg. Daily Sessions', value: data.avgDailySessions.toFixed(1) },
            { color: '#1976D2', label: 'Peak Day', value: formatPeakDay(data.peakDay, data.peakDayCount) },
            { color: '#F0A500', label: 'Most Active Hour', value: data.mostActiveHour ?? '—' },
          ].map(({ color, label, value }) => (
            <Box
              key={label}
              sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                py: 0.75, borderTop: '1px solid rgba(0,0,0,0.06)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
                <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{label}</Typography>
              </Box>
              <Typography sx={{ fontWeight: 600, fontSize: 13, color: 'text.primary' }}>{value}</Typography>
            </Box>
          ))}

          {/* Daily Sessions Trend sparkline — fills the empty space below the stats */}
          {daily && daily.isConfigured && daily.data.length > 1 && (
            <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid rgba(0,0,0,0.06)' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#002C5F' }}>
                  Sessions Trend
                </Typography>
                <Typography sx={{ fontSize: 10, color: 'text.disabled' }}>
                  per day
                </Typography>
              </Box>
              <LineChart
                height={90}
                xAxis={[{
                  scaleType: 'point',
                  data: daily.data.map((d) =>
                    d.date.length === 8
                      ? dayjs(d.date, 'YYYYMMDD').format('D')
                      : dayjs(d.date).format('D')
                  ),
                  tickLabelStyle: { fontSize: 9, fill: '#9E9E9E' },
                  tickNumber: Math.min(daily.data.length, 7),
                }]}

                series={[{
                  data: daily.data.map((d) => d.sessions),
                  color: '#002C5F',
                  area: true,
                  showMark: false,
                }]}
                margin={{ top: 8, bottom: 22, left: 32, right: 8 }}
                slotProps={{ legend: { hidden: true } }}
                sx={{
                  '& .MuiAreaElement-root': { fillOpacity: 0.15 },
                  '& .MuiLineElement-root': { strokeWidth: 1.5 },
                }}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  )
}

export default MostAccessedFeaturesChart
